import { useState, useEffect, useRef } from 'react'
import { Save, User, Bell, Shield, Palette, Globe, Sparkles, Users, Link2, UsersRound, Webhook, FileText, RefreshCw, Send, Upload, Eye, EyeOff, Check, Building2, CreditCard, Camera, Key } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface UserData {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  phone: string
  role: string
  avatar: string | null
  bio: string
  company: {
    id: number
    name: string
  } | null
  date_joined: string
}

interface PasswordData {
  old_password: string
  new_password: string
  new_password_confirm: string
}

const Settings = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    bio: ''
  })

  const [passwordData, setPasswordData] = useState<PasswordData>({
    old_password: '',
    new_password: '',
    new_password_confirm: ''
  })

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  })

  const [theme, setTheme] = useState('light')
  const [showPasswordModal, setShowPasswordModal] = useState(false)

  // Estilos reutilizáveis - Glassmorphism
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3) var(--space-4)',
    background: 'var(--glass-bg)',
    backdropFilter: 'var(--glass-blur)',
    WebkitBackdropFilter: 'var(--glass-blur)',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-xl)',
    fontSize: 'var(--text-sm)',
    color: 'var(--text-primary)',
    outline: 'none',
    transition: 'all var(--transition-fast)'
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--text-sm)',
    fontWeight: '600',
    marginBottom: 'var(--space-2)',
    color: 'var(--text-primary)'
  }

  const personalTabs = [
    { id: 'profile', label: 'Meu perfil', icon: User },
    { id: 'appearance', label: 'Preferências', icon: Palette },
    { id: 'company', label: 'Empresas', icon: Building2 },
    { id: 'billing', label: 'Faturamento', icon: CreditCard },
  ]

  const systemTabs = [
    { id: 'users', label: 'Usuários', icon: Users, path: '/company-users' },
    { id: 'teams', label: 'Equipes', icon: UsersRound, path: '/teams' },
    { id: 'distribution', label: 'Distribuição de Leads', icon: RefreshCw, path: '/lead-distribution' },
    { id: 'integrations', label: 'Integrações', icon: Link2, path: '/integrations' },
    { id: 'webhooks', label: 'Webhooks de Entrada', icon: Webhook, path: '/webhooks' },
    { id: 'outgoing-webhooks', label: 'Webhooks de Saida', icon: Send, path: '/outgoing-webhooks' },
    { id: 'custom-fields', label: 'Campos Personalizados', icon: FileText, path: '/custom-fields' },
    { id: 'ai', label: 'Inteligência Artificial', icon: Sparkles, path: '/ai-settings' },
    { id: 'api-keys', label: 'Chaves de API', icon: Key, path: '/api-keys' },
  ]

  useEffect(() => {
    loadUserData()
    const savedTheme = localStorage.getItem('theme') || 'light'
    setTheme(savedTheme)
  }, [])

  const loadUserData = async () => {
    try {
      setLoading(true)
      const response = await api.get('/auth/users/me/')
      const user = response.data
      setUserData(user)
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        bio: user.bio || ''
      })
      if (user.avatar) {
        setAvatarPreview(user.avatar)
      }
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error)
      alert('Erro ao carregar dados do perfil')
    } finally {
      setLoading(false)
    }
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setAvatarFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSaveProfile = async () => {
    try {
      setSaving(true)

      // Criar FormData para enviar com o avatar
      const formDataToSend = new FormData()
      formDataToSend.append('first_name', formData.first_name)
      formDataToSend.append('last_name', formData.last_name)
      formDataToSend.append('phone', formData.phone)
      formDataToSend.append('bio', formData.bio)

      if (avatarFile) {
        formDataToSend.append('avatar', avatarFile)
      }

      await api.patch(`/auth/users/${userData?.id}/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      alert('✅ Perfil atualizado com sucesso!')
      loadUserData()
    } catch (error: any) {
      console.error('Erro ao salvar perfil:', error)
      alert('❌ Erro ao salvar perfil: ' + (error.response?.data?.detail || 'Erro desconhecido'))
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.new_password_confirm) {
      alert('❌ As senhas não coincidem')
      return
    }

    if (passwordData.new_password.length < 8) {
      alert('❌ A nova senha deve ter no mínimo 8 caracteres')
      return
    }

    try {
      setSaving(true)
      await api.post('/auth/users/change_password/', {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
        new_password_confirm: passwordData.new_password_confirm
      })

      alert('✅ Senha alterada com sucesso!')
      setPasswordData({
        old_password: '',
        new_password: '',
        new_password_confirm: ''
      })
    } catch (error: any) {
      console.error('Erro ao alterar senha:', error)
      const message = error.response?.data?.old_password?.[0] ||
                     error.response?.data?.new_password?.[0] ||
                     error.response?.data?.detail ||
                     'Erro ao alterar senha'
      alert('❌ ' + message)
    } finally {
      setSaving(false)
    }
  }

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme)
    localStorage.setItem('theme', newTheme)
    // TODO: Aplicar tema na aplicação
    alert(`Tema "${newTheme}" salvo! (Funcionalidade de aplicação de tema será implementada em breve)`)
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid var(--border-light)',
          borderTopColor: 'var(--primary-600)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
      </div>
    )
  }

  return (
    <div style={{ padding: 'var(--space-6)' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{
          fontSize: 'var(--text-3xl)',
          fontWeight: 'bold',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-2)'
        }}>
          Configurações
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
          Gerencie suas preferências e configurações do sistema
        </p>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
        {/* Sidebar Tabs */}
        <div style={{
          width: '250px',
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          WebkitBackdropFilter: 'var(--glass-blur)',
          borderRadius: 'var(--radius-2xl)',
          border: '1px solid var(--glass-border)',
          padding: 'var(--space-4)',
          height: 'fit-content',
          boxShadow: 'var(--shadow-glass)'
        }}>
          {/* Configurações Pessoais */}
          <p style={{
            fontSize: '10px',
            fontWeight: '600',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: 'var(--space-2)',
            paddingLeft: 'var(--space-2)',
          }}>
            Pessoal
          </p>
          {personalTabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-2)',
                  backgroundColor: activeTab === tab.id ? 'var(--primary-50)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--primary-600)' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  transition: 'all 0.2s ease'
                }}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            )
          })}

          {/* Separador */}
          <div style={{
            height: '1px',
            background: 'var(--border-light)',
            margin: 'var(--space-4) 0',
          }} />

          {/* Configurações do Sistema */}
          <p style={{
            fontSize: '10px',
            fontWeight: '600',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: 'var(--space-2)',
            paddingLeft: 'var(--space-2)',
          }}>
            Sistema
          </p>
          {systemTabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-2)',
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Content Area */}
        <div style={{
          flex: 1,
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          WebkitBackdropFilter: 'var(--glass-blur)',
          borderRadius: 'var(--radius-2xl)',
          border: '1px solid var(--glass-border)',
          padding: 'var(--space-6)',
          boxShadow: 'var(--shadow-glass)'
        }}>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div>
              {/* Avatar Section - Centered */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 'var(--space-8) var(--space-6)',
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                borderRadius: 'var(--radius-2xl)',
                border: '1px solid var(--glass-border)',
                marginBottom: 'var(--space-6)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Background decoration */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  right: '-20%',
                  width: '400px',
                  height: '400px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }} />

                {/* Avatar with hover effect */}
                <div style={{ position: 'relative', marginBottom: 'var(--space-4)' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    style={{ display: 'none' }}
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: 'var(--radius-full)',
                      background: avatarPreview ? `url(${avatarPreview}) center/cover` : 'var(--bg-gradient)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'var(--text-4xl)',
                      fontWeight: '700',
                      color: 'white',
                      cursor: 'pointer',
                      border: '4px solid white',
                      boxShadow: 'var(--shadow-xl)',
                      position: 'relative',
                      transition: 'all var(--transition-base)',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-primary-lg)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-xl)'
                    }}
                  >
                    {!avatarPreview && (userData?.first_name?.[0]?.toUpperCase() || 'U')}
                    {/* Overlay on hover */}
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0, 0, 0, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity var(--transition-fast)'
                    }}
                    className="avatar-overlay">
                      <Camera size={32} color="white" />
                    </div>
                  </div>
                </div>

                <h2 style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'bold',
                  color: 'var(--text-primary)',
                  marginBottom: 'var(--space-1)',
                  textAlign: 'center'
                }}>
                  {userData?.full_name}
                </h2>
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-secondary)',
                  marginBottom: 'var(--space-1)',
                  textAlign: 'center'
                }}>
                  {userData?.email}
                </p>
                <p style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-tertiary)',
                  textAlign: 'center'
                }}>
                  Membro desde {new Date(userData?.date_joined || '').toLocaleDateString('pt-BR')}
                </p>
              </div>

              {/* Grid Layout for Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>
                {/* Personal Info Card */}
                <div style={{
                  gridColumn: '1 / -1',
                  padding: 'var(--space-6)',
                  background: 'var(--glass-bg)',
                  backdropFilter: 'var(--glass-blur)',
                  WebkitBackdropFilter: 'var(--glass-blur)',
                  borderRadius: 'var(--radius-xl)',
                  border: '1px solid var(--glass-border)',
                  boxShadow: 'var(--shadow-glass)'
                }}>
                  <h3 style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: '600',
                    marginBottom: 'var(--space-1)',
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                  }}>
                    <User size={20} style={{ color: 'var(--primary-600)' }} />
                    Informações Pessoais
                  </h3>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-tertiary)',
                    marginBottom: 'var(--space-5)'
                  }}>
                    Atualize suas informações de perfil
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                    <div>
                      <label style={labelStyle}>Nome</label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--color-primary-400)'
                          e.target.style.background = 'var(--glass-bg-strong)'
                          e.target.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.15)'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--glass-border)'
                          e.target.style.background = 'var(--glass-bg)'
                          e.target.style.boxShadow = 'none'
                        }}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Sobrenome</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--color-primary-400)'
                          e.target.style.background = 'var(--glass-bg-strong)'
                          e.target.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.15)'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--glass-border)'
                          e.target.style.background = 'var(--glass-bg)'
                          e.target.style.boxShadow = 'none'
                        }}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Telefone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+55 (11) 99999-9999"
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--color-primary-400)'
                          e.target.style.background = 'var(--glass-bg-strong)'
                          e.target.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.15)'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--glass-border)'
                          e.target.style.background = 'var(--glass-bg)'
                          e.target.style.boxShadow = 'none'
                        }}
                        style={inputStyle}
                      />
                    </div>

                    <div>
                      <label style={labelStyle}>Email</label>
                      <input
                        type="email"
                        value={userData?.email || ''}
                        disabled
                        style={{
                          ...inputStyle,
                          background: 'rgba(0, 0, 0, 0.15)',
                          cursor: 'not-allowed',
                          opacity: 0.7
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Security Card */}
                <div style={{
                  padding: 'var(--space-6)',
                  background: 'var(--glass-bg)',
                  backdropFilter: 'var(--glass-blur)',
                  WebkitBackdropFilter: 'var(--glass-blur)',
                  borderRadius: 'var(--radius-xl)',
                  border: '1px solid var(--glass-border)',
                  boxShadow: 'var(--shadow-glass)'
                }}>
                  <h3 style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: '600',
                    marginBottom: 'var(--space-1)',
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                  }}>
                    <Shield size={20} style={{ color: 'var(--primary-600)' }} />
                    Segurança
                  </h3>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-tertiary)',
                    marginBottom: 'var(--space-5)'
                  }}>
                    Gerencie sua senha de acesso
                  </p>

                  <button
                    onClick={() => setShowPasswordModal(true)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-3) var(--space-4)',
                      backgroundColor: 'var(--primary-600)',
                      background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--secondary-600) 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius-lg)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all var(--transition-fast)',
                      boxShadow: 'var(--shadow-primary)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-primary-lg)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-primary)'
                    }}
                  >
                    <Shield size={18} />
                    Alterar Senha
                  </button>

                  <div style={{
                    marginTop: 'var(--space-4)',
                    padding: 'var(--space-4)',
                    background: 'rgba(99, 102, 241, 0.05)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid rgba(99, 102, 241, 0.1)'
                  }}>
                    <p style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-tertiary)',
                      lineHeight: '1.5'
                    }}>
                      💡 Use uma senha forte com pelo menos 8 caracteres, incluindo letras, números e símbolos.
                    </p>
                  </div>
                </div>

                {/* Account Info Card */}
                <div style={{
                  padding: 'var(--space-6)',
                  background: 'var(--glass-bg)',
                  backdropFilter: 'var(--glass-blur)',
                  WebkitBackdropFilter: 'var(--glass-blur)',
                  borderRadius: 'var(--radius-xl)',
                  border: '1px solid var(--glass-border)',
                  boxShadow: 'var(--shadow-glass)'
                }}>
                  <h3 style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: '600',
                    marginBottom: 'var(--space-1)',
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                  }}>
                    <Bell size={20} style={{ color: 'var(--primary-600)' }} />
                    Informações da Conta
                  </h3>
                  <p style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-tertiary)',
                    marginBottom: 'var(--space-5)'
                  }}>
                    Detalhes da sua conta
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'var(--space-3)',
                      background: 'rgba(0, 0, 0, 0.05)',
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>Função</span>
                      <span style={{ fontSize: 'var(--text-sm)', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {userData?.role === 'admin' ? 'Administrador' :
                         userData?.role === 'manager' ? 'Gerente' :
                         userData?.role === 'agent' ? 'Agente' :
                         userData?.role === 'viewer' ? 'Visualizador' : userData?.role}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'var(--space-3)',
                      background: 'rgba(0, 0, 0, 0.05)',
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>Status</span>
                      <span style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: '600',
                        color: 'var(--success-700)',
                        background: 'var(--success-100)',
                        padding: 'var(--space-1) var(--space-3)',
                        borderRadius: 'var(--radius-full)'
                      }}>
                        Ativo
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button - Fixed at bottom */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--space-3)',
                paddingTop: 'var(--space-4)',
                borderTop: '1px solid var(--border-light)'
              }}>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-3) var(--space-6)',
                    backgroundColor: 'var(--primary-600)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: '600',
                    cursor: saving ? 'wait' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                    transition: 'all var(--transition-fast)',
                    boxShadow: 'var(--shadow-primary)'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-primary-lg)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'var(--shadow-primary)'
                    }
                  }}
                >
                  <Save size={18} />
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div>
              <h2 style={{
                fontSize: 'var(--text-xl)',
                fontWeight: 'bold',
                marginBottom: 'var(--space-2)',
                color: 'var(--text-primary)'
              }}>
                Preferências
              </h2>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-tertiary)',
                marginBottom: 'var(--space-6)'
              }}>
                Personalize a aparência do app selecionando o tema
              </p>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                  marginBottom: 'var(--space-3)',
                  color: 'var(--text-primary)'
                }}>
                  Tema
                </label>

                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  {[
                    { value: 'light', label: 'Claro' },
                    { value: 'dark', label: 'Escuro' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleThemeChange(option.value)}
                      style={{
                        flex: 1,
                        padding: 'var(--space-4)',
                        background: theme === option.value ? 'var(--primary-50)' : 'var(--glass-bg)',
                        border: `2px solid ${theme === option.value ? 'var(--primary-600)' : 'var(--glass-border)'}`,
                        borderRadius: 'var(--radius-lg)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: '500',
                        color: theme === option.value ? 'var(--primary-600)' : 'var(--text-primary)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                    >
                      {theme === option.value && (
                        <div style={{
                          position: 'absolute',
                          top: 'var(--space-2)',
                          right: 'var(--space-2)',
                          width: '20px',
                          height: '20px',
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--primary-600)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Check size={14} color="white" />
                        </div>
                      )}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                flexDirection: 'column',
                gap: 'var(--space-4)'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: 'var(--primary-600)',
                  background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--secondary-600) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'var(--shadow-primary-lg)'
                }}>
                  <CreditCard size={40} style={{ color: 'white', strokeWidth: 2 }} />
                </div>
                <h2 style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'bold',
                  color: 'var(--text-primary)',
                  textAlign: 'center'
                }}>
                  Faturamento e Planos
                </h2>
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-tertiary)',
                  textAlign: 'center',
                  maxWidth: '500px'
                }}>
                  Gerencie seu plano, visualize faturas e atualize as informações de pagamento.
                </p>
                <button
                  onClick={() => navigate('/billing')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-3) var(--space-6)',
                    backgroundColor: 'var(--primary-600)',
                    background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--secondary-600) 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all var(--transition-fast)',
                    boxShadow: 'var(--shadow-primary)',
                    marginTop: 'var(--space-4)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = 'var(--shadow-primary-lg)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'var(--shadow-primary)'
                  }}
                >
                  <CreditCard size={18} />
                  Acessar Faturamento
                </button>
              </div>
            </div>
          )}

          {/* Company Tab */}
          {activeTab === 'company' && (
            <div>
              <h2 style={{
                fontSize: 'var(--text-xl)',
                fontWeight: 'bold',
                marginBottom: 'var(--space-2)',
                color: 'var(--text-primary)'
              }}>
                Empresas
              </h2>
              <p style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-tertiary)',
                marginBottom: 'var(--space-6)'
              }}>
                Empresas que você possui ou participa
              </p>

              {userData?.company ? (
                <div style={{
                  padding: 'var(--space-5)',
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-xl)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--primary-100)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Building2 size={24} color="var(--primary-600)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: 'var(--text-base)',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: 'var(--space-1)'
                    }}>
                      {userData.company.name}
                    </h3>
                    <p style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-tertiary)'
                    }}>
                      Nicho: ComercioDeVendas
                    </p>
                  </div>
                  <div style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-tertiary)'
                  }}>
                    {new Date(userData.date_joined).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              ) : (
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-tertiary)',
                  textAlign: 'center',
                  padding: 'var(--space-8)'
                }}>
                  Você não está associado a nenhuma empresa
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 'var(--space-4)'
        }}>
          <div style={{
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'var(--glass-blur)',
            WebkitBackdropFilter: 'var(--glass-blur)',
            borderRadius: 'var(--radius-2xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-6)',
            maxWidth: '500px',
            width: '100%',
            boxShadow: 'var(--shadow-glass)'
          }}>
            <h2 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)'
            }}>
              Alterar senha
            </h2>
            <p style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--text-tertiary)',
              marginBottom: 'var(--space-6)'
            }}>
              Digite sua senha atual e escolha uma nova senha
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div>
                <label style={labelStyle}>Senha atual</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.old ? 'text' : 'password'}
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-primary-400)'
                      e.currentTarget.style.background = 'var(--glass-bg-strong)'
                      e.currentTarget.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.15)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--glass-border)'
                      e.currentTarget.style.background = 'var(--glass-bg)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                    style={{
                      ...inputStyle,
                      paddingRight: 'var(--space-10)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, old: !showPasswords.old })}
                    style={{
                      position: 'absolute',
                      right: 'var(--space-3)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-tertiary)',
                      padding: '0'
                    }}
                  >
                    {showPasswords.old ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Nova senha</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-primary-400)'
                      e.currentTarget.style.background = 'var(--glass-bg-strong)'
                      e.currentTarget.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.15)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--glass-border)'
                      e.currentTarget.style.background = 'var(--glass-bg)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                    style={{
                      ...inputStyle,
                      paddingRight: 'var(--space-10)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    style={{
                      position: 'absolute',
                      right: 'var(--space-3)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-tertiary)',
                      padding: '0'
                    }}
                  >
                    {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Confirmar nova senha</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.new_password_confirm}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password_confirm: e.target.value })}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-primary-400)'
                      e.currentTarget.style.background = 'var(--glass-bg-strong)'
                      e.currentTarget.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.15)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'var(--glass-border)'
                      e.currentTarget.style.background = 'var(--glass-bg)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                    style={{
                      ...inputStyle,
                      paddingRight: 'var(--space-10)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    style={{
                      position: 'absolute',
                      right: 'var(--space-3)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-tertiary)',
                      padding: '0'
                    }}
                  >
                    {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              marginTop: 'var(--space-6)',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowPasswordModal(false)
                  setPasswordData({
                    old_password: '',
                    new_password: '',
                    new_password_confirm: ''
                  })
                }}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  await handleChangePassword()
                  if (passwordData.old_password && passwordData.new_password === passwordData.new_password_confirm) {
                    setShowPasswordModal(false)
                  }
                }}
                disabled={saving || !passwordData.old_password || !passwordData.new_password || !passwordData.new_password_confirm}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--primary-600)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: '500',
                  cursor: saving ? 'wait' : 'pointer',
                  opacity: (saving || !passwordData.old_password || !passwordData.new_password || !passwordData.new_password_confirm) ? 0.5 : 1
                }}
              >
                {saving ? 'Alterando...' : 'Alterar senha'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global styles for hover effects */}
      <style>{`
        .avatar-overlay {
          transition: opacity var(--transition-fast);
        }

        div:hover .avatar-overlay {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  )
}

export default Settings
